import type { ReactNode } from 'react';
import { T, Var, useGT } from 'gt-react';
import type { WorkbookRun } from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import type { WorkbookEditor } from './useWorkbookEditor';
import { useWorkbookRunTime } from './workbookRunTime';
import { WorkbookRunInsights } from './WorkbookRunInsights';
import {
  formatElapsed,
  runSheetTimes,
  useElapsed,
} from './workbookRunProgress';
import { WorkbookItemIcon } from './workbookItemIcon';
import { workbookItemIconKind } from './workbookItemIconKind';

/**
 * The Runs tab: every listed Run, newest first, with its error when it failed
 * and the integrations' actions on it (the `run-actions` surface).
 */
export function WorkbookRunHistory({
  editor,
  renderActions,
}: {
  editor: Pick<WorkbookEditor, 'definition'> & {
    runs: Pick<WorkbookEditor['runs'], 'data' | 'isPending' | 'error'>;
  };
  renderActions?: (run: WorkbookRun) => ReactNode;
}) {
  const { runs } = editor;
  const runTime = useWorkbookRunTime(editor.definition);
  return (
    <section className="flex min-w-0 flex-col gap-3">
      <h2 className="font-medium">
        <T context="workbook run history heading">Run history</T>
      </h2>
      {runs.isPending ? (
        <p className="text-sm text-muted-foreground">
          <T context="workbook loading runs">Loading runs…</T>
        </p>
      ) : runs.error ? (
        <p role="alert" className="text-sm text-destructive">
          {runs.error.message}
        </p>
      ) : runs.data?.length ? (
        <ol className="flex flex-col gap-2">
          {runs.data.map((run) => (
            <li key={run.id} className="rounded border p-2 text-xs">
              <WorkbookRunDetails run={run} mode={runTime.mode(run)} />
              {run.error && (
                <p className="mt-1 text-destructive">{run.error}</p>
              )}
              <WorkbookRunInsights run={run} definition={editor.definition} />
              <WorkbookRunTimes run={run} definition={editor.definition} />
              {renderActions && (
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {renderActions(run)}
                </div>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">
          <T context="workbook no runs">No runs yet.</T>
        </p>
      )}
    </section>
  );
}

/** Where a Run's time went: each Worksheet it computed by name, slowest first. */
function WorkbookRunTimes({
  run,
  definition,
}: {
  run: WorkbookRun;
  definition: WorkbookEditor['definition'];
}) {
  const gt = useGT();
  const times = runSheetTimes(
    run,
    (sheetId) =>
      definition.sheets.find((sheet) => sheet.id === sheetId)?.name ??
      gt('Removed worksheet', { context: 'workbook run timing removed sheet' }),
  );
  if (!times) return null;
  return (
    <details className="mt-1">
      <summary className="cursor-pointer text-muted-foreground">
        <T context="workbook run timing summary">
          Worksheet times · <Var>{times.total}</Var>
        </T>
      </summary>
      <table className="mt-1 ml-4">
        <tbody>
          {times.rows.map((row) => (
            <tr key={row.sheetId}>
              <td className="max-w-64 truncate pr-6">
                <WorkbookItemIcon
                  kind={
                    workbookItemIconKind(definition, row.sheetId) ?? 'worksheet'
                  }
                  className="mr-1.5 inline size-3.5 align-[-2px] text-muted-foreground"
                  aria-hidden
                />
                {row.name}
              </td>
              <td className="text-right text-muted-foreground tabular-nums">
                {row.duration}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

/** A Run's status as a Badge: calm for the usual outcome, red only for a failure. */
function runStatusVariant(
  status: WorkbookRun['status'],
): 'outline' | 'secondary' | 'destructive' {
  if (status === 'failed') return 'destructive';
  if (status === 'running' || status === 'queued') return 'secondary';
  return 'outline';
}

function WorkbookRunDetails({ run, mode }: { run: WorkbookRun; mode: string }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant={runStatusVariant(run.status)} className="font-medium">
        <WorkbookRunStatus status={run.status} />
      </Badge>
      <span>
        {new Date(run.createdAt).toLocaleString()} · {mode}
        {' · '}
        {run.triggeredBy ? (
          <T context="workbook run initiating user">
            Run by <Var>{run.triggeredBy.name}</Var>
          </T>
        ) : (
          <T context="workbook run missing initiating user">User unavailable</T>
        )}
        {run.origin === 'workflow' && (
          <>
            {' · '}
            <T context="workbook run started by a workflow">From a workflow</T>
          </>
        )}
        {run.origin === 'agent' && (
          <>
            {' · '}
            <T context="workbook run started by an agent">From an agent</T>
          </>
        )}
      </span>
    </div>
  );
}

export function WorkbookRunStatus({ status }: { status: string }) {
  const gt = useGT();
  const labels: Record<string, string> = {
    queued: gt('Queued', { context: 'workbook run status' }),
    running: gt('Running', { context: 'workbook run status' }),
    completed: gt('Completed', { context: 'workbook run status' }),
    failed: gt('Failed', { context: 'workbook run status' }),
  };
  return <>{labels[status] ?? status}</>;
}

/** How long a running Run has executed so far, as " · 4s"; nothing while it waits or once it ends. */
export function WorkbookRunningTotal({
  run,
}: {
  run: Pick<WorkbookRun, 'status' | 'timing'>;
}) {
  const elapsed = useElapsed(
    run.status === 'running' ? run.timing?.startedAt : undefined,
  );
  if (elapsed === undefined) return null;
  return <span className="tabular-nums"> · {formatElapsed(elapsed)}</span>;
}
